/**
 * Normalización y cotejo de nombres de comuna.
 *
 * La colección tiene el mismo nombre escrito de varias formas conviviendo
 * ("Ñuñoa", "NUÑOA", "Nunoa"), así que cualquier comparación tiene que ser
 * insensible a mayúsculas y a tildes. El backend ya asume eso al FILTRAR
 * (`buildLooseTextFilter` en microservice-packages); acá se aplica el mismo
 * criterio al VALIDAR, para no rechazar una comuna que después el listado sí
 * encontraría.
 *
 * Es una réplica del criterio de `superadmin/.../plantillaAltas.js`
 * (`sinAcentos` + `indiceDeComunas`). Vive acá y no se importa de allá a
 * propósito: aquel módulo es de un dominio de UI puntual y esto lo consume un
 * componente compartido.
 */

/** Minúsculas, sin tildes, sin espacios de más. */
export function normalizeCommune(name) {
  return String(name ?? "")
    .normalize("NFD")
    // Rango de diacríticos combinantes. Va explícito y no como \p{M} porque
    // este archivo lo compila CRA para navegadores viejos.
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

/**
 * Índice de comunas válidas, listo para cotejar.
 * @param {string[]} names
 * @returns {Set<string>} formas normalizadas; vacío si no hay nombres.
 */
export function buildCommuneIndex(names = []) {
  const index = new Set();
  for (const name of names) {
    const key = normalizeCommune(name);
    if (key) index.add(key);
  }
  return index;
}

/**
 * ¿Está la comuna dentro del índice?
 *
 * Un índice VACÍO devuelve siempre `true`: significa "no se pudo determinar la
 * cobertura", y en ese caso no se bloquea la carga. Es una validación de ayuda,
 * no una compuerta de seguridad — el backend nunca validó la comuna, así que
 * dejarla pasar es exactamente la conducta de hoy, mientras que rechazarla por
 * un endpoint caído le frenaría la operación al comercio.
 */
export function isCommuneCovered(name, index) {
  if (!index || index.size === 0) return true;
  return index.has(normalizeCommune(name));
}
